export type OpeningStatus = "draft" | "assigned" | "counted" | "posted" | "cancelled";
export type OpeningBatch = { quantity: number; expiresOn: string | null };
export type OpeningItem = { id: string; lotId?: string | null; productId: string; code: string; productName: string; plannedQuantity: number; actualQuantity: number | null; unitCostMinor?: number | null; valuationStatus?: "known" | "unknown"; valuationVersion?: number; sourceExclusionReason?: "negative_stock"; sourceObservedQuantities?: string[]; batches: OpeningBatch[] };
export type OpeningDocument = { id: string; number: string; status: OpeningStatus; version: number; documentDate: string; warehouseId: string; warehouseName: string; legalEntityId: string; assigneeUserId: string | null; items: OpeningItem[]; totalCostMinor?: number | null; valuationComplete?: boolean; readOnly?: boolean; canCount?: boolean; canPost?: boolean; allowUnknownExpiry?: boolean; hasDiscrepancy?: boolean; sourceAccountId?: string; sourceSha256?: string; effectiveSourceSha256?: string; sourceCapturedAt?: string; sourceCutoffAt?: string; sourceMappingSha256?: string; sourceRevisionVersion?: number };
export type OpeningEvent = "assign" | "count" | "valuation" | "post" | "cancel";
export type OpeningCommand = Readonly<{ documentId: string; event: OpeningEvent; key: string; version: number; body: string }>;
export type OpeningCreateCommand = Readonly<{ event: "create"; key: string; body: string }>;
export type OpeningRequest = OpeningCommand | OpeningCreateCommand;
export type OpeningRecovery = (Omit<OpeningCommand, "body"> | Omit<OpeningCreateCommand, "body">) & { readonly body?: string };
export type OpeningSourceRequest = { warehouseId: string; documentDate: string; sourceAccountId: string; sourceSha256: string; items: { productId: string; plannedQuantity: number; unitCostMinor?: number | null; sourceRows: { sourceProductId: string; sourceWarehouseId: string; quantity: string }[] }[] };
export type OpeningSourcePackage = Readonly<{ format: "pharmhub-opening-stock-v1"; warehouseName: string; request: OpeningSourceRequest }>;
export const openingPackageMaxBytes = 5_000_000;
const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export const openingRecoveryPrefix = "pharmhub:opening-stock-command:";
export function openingRecoveryKey(userId: string, role: string): string {
  return `${openingRecoveryPrefix}${encodeURIComponent(userId)}:${role}`;
}
export function restoreOpeningCommand(raw: string, role: string): OpeningRecovery {
  const value = JSON.parse(raw) as OpeningRecovery;
  const events = role === "admin" ? ["create", "assign", "count", "valuation", "post", "cancel"] : role === "operator" ? ["count", "post"] : [];
  if (!value || typeof value !== "object" || typeof value.key !== "string" || !uuid.test(value.key)
    || !events.includes(value.event)
    || (value.event === "create" ? !["body,event,key", "event,key"].includes(Object.keys(value).sort().join())
      : !["body,documentId,event,key,version", "documentId,event,key,version"].includes(Object.keys(value).sort().join())
        || typeof value.documentId !== "string" || !uuid.test(value.documentId) || !Number.isSafeInteger(value.version) || value.version < 1)
    || value.body !== undefined && (typeof value.body !== "string" || value.body.length > openingPackageMaxBytes)) throw new Error("Invalid recovery envelope");
  if (value.body !== undefined) JSON.parse(value.body);
  return Object.freeze(value);
}
export function redactOpeningCommand(raw: string): string {
  try {
    const value = restoreOpeningCommand(raw, "admin");
    return value.event === "create" ? JSON.stringify({event: value.event, key: value.key})
      : JSON.stringify({documentId: value.documentId, event: value.event, key: value.key, version: value.version});
  } catch { return '{"unresolved":true}'; }
}

export function openingQuantity(raw: string): number | null {
  if (!/^\d+$/.test(raw.trim())) return null;
  const value = Number(raw.trim());
  return Number.isSafeInteger(value) && value >= 0 && value <= 2147483647 ? value : null;
}

export function openingMoney(raw: string): number | null {
  const match = /^(\d+)(?:[,.](\d{1,2}))?$/.exec(raw.trim());
  if (!match) return null;
  const minor = Number(match[1]) * 100 + Number((match[2] || "").padEnd(2, "0"));
  return Number.isSafeInteger(minor) && minor >= 0 ? minor : null;
}

export function openingExpiry(raw: string): string | null {
  const match = /^(0[1-9]|1[0-2])\/(\d{4})$/.exec(raw.trim());
  if (!match || Number(match[2]) < 2000 || Number(match[2]) > 2199) return null;
  const month = Number(match[1]), year = Number(match[2]);
  const last = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return `${year}-${match[1]}-${String(last).padStart(2, "0")}`;
}

export type OpeningCountDraft = Record<string, { quantity: string; expiry: string }[]>;
export type OpeningCountPayload = { lines: { lineId: string; batches: OpeningBatch[] }[] };

// Only an explicitly supported fresh document may turn a blank into NULL.
// Invalid nonempty input must never silently become an unknown expiry.
export function openingCountPayload(document: OpeningDocument, role: string, drafts: OpeningCountDraft): OpeningCountPayload | null {
  if (!openingPermissions(document, role).canCount) return null;
  const lines: OpeningCountPayload["lines"] = [];
  for (const item of document.items) {
    const batches: OpeningBatch[] = [], months = new Set<string | null>();
    let total = 0;
    for (const draft of drafts[item.id] || []) {
      const quantity = openingQuantity(draft.quantity), blank = draft.expiry.trim() === "";
      const expiresOn = blank ? null : openingExpiry(draft.expiry);
      if (quantity === null || quantity < 1 || quantity > 999_999
        || expiresOn === null && (!blank || document.allowUnknownExpiry !== true)
        || months.has(expiresOn)) return null;
      total += quantity;
      if (total > 999_999) return null;
      months.add(expiresOn);
      batches.push({ quantity, expiresOn });
    }
    lines.push({ lineId: item.id, batches });
  }
  return { lines };
}

export function openingCommand(document: OpeningDocument, event: OpeningEvent, payload: unknown, key: string): OpeningCommand {
  return Object.freeze({ documentId: document.id, event, key, version: document.version, body: JSON.stringify(payload) });
}

export function openingHasDiscrepancy(document: OpeningDocument): boolean {
  return document.hasDiscrepancy ?? document.items.some(item => item.actualQuantity !== null && item.actualQuantity !== item.plannedQuantity);
}

export function openingPlannedTotalMinor(document: OpeningDocument, role: string): number | null {
  if (role !== "admin") return null;
  let total = 0;
  for (const item of document.items) {
    if (typeof item.unitCostMinor !== "number" || !Number.isSafeInteger(item.unitCostMinor) || item.unitCostMinor < 0
      || !Number.isSafeInteger(item.plannedQuantity) || item.plannedQuantity < 0) return null;
    total += item.plannedQuantity * item.unitCostMinor;
    if (!Number.isSafeInteger(total)) return null;
  }
  return total;
}

export function openingPermissions(document: OpeningDocument, role: string) {
  const mutable = document.readOnly === false && (role === "admin" || role === "operator");
  return { mutable, canCount: mutable && document.canCount === true && document.status === "assigned",
    canPost: mutable && document.canPost === true && document.status === "counted" };
}

export function openingCreateCommand(value: OpeningSourcePackage, key: string): OpeningCreateCommand {
  return Object.freeze({ event: "create", key, body: JSON.stringify(value.request) });
}

export function openingOutcomeDocumentId(command: OpeningRecovery, value: OpeningDocument | undefined): string | null {
  if (!value || typeof value.id !== "string" || !uuid.test(value.id) || !Number.isSafeInteger(value.version)
    || value.version < 1 || !Array.isArray(value.items)) return null;
  if (command.event !== "create") return value.id === command.documentId ? value.id : null;
  if (command.body !== undefined) {
    try {
      const original = JSON.parse(command.body) as OpeningSourceRequest;
      if (value.warehouseId !== original.warehouseId || value.sourceAccountId !== original.sourceAccountId
        || value.sourceSha256 !== original.sourceSha256) return null;
    } catch { return null; }
  }
  // A redacted key-only marker is recovered only from the actor-scoped
  // command endpoint; the endpoint itself binds that key to its original DTO.
  return value.id;
}

export function parseOpeningSourcePackage(raw: string, role: string): OpeningSourcePackage {
  const fail = () => { throw new Error("Файл начальных остатков не прошёл проверку. Используйте подготовленный пакет для одного склада."); };
  if (role !== "admin" || raw.length > openingPackageMaxBytes) return fail();
  let value: unknown;
  try { value = JSON.parse(raw); } catch { return fail(); }
  const object = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null && !Array.isArray(v);
  const keys = (v: Record<string, unknown>, required: string[], optional: string[] = []) => required.every(k => k in v) && Object.keys(v).every(k => required.includes(k) || optional.includes(k));
  const identifier = (v: unknown): v is string => typeof v === "string" && uuid.test(v) && v !== "00000000-0000-0000-0000-000000000000";
  if (!object(value) || !keys(value, ["format", "warehouseName", "request"]) || value.format !== "pharmhub-opening-stock-v1"
    || typeof value.warehouseName !== "string" || !value.warehouseName.trim() || value.warehouseName.length > 240 || !object(value.request)) return fail();
  const request = value.request;
  if (!keys(request, ["warehouseId", "documentDate", "sourceAccountId", "sourceSha256", "items"])
    || !identifier(request.warehouseId) || !identifier(request.sourceAccountId)
    || typeof request.sourceSha256 !== "string" || !/^[0-9a-f]{64}$/.test(request.sourceSha256)
    || typeof request.documentDate !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(request.documentDate)
    || request.documentDate.startsWith("0000-") || !Number.isFinite(new Date(`${request.documentDate}T00:00:00Z`).getTime())
    || new Date(`${request.documentDate}T00:00:00Z`).toISOString().slice(0, 10) !== request.documentDate
    || !Array.isArray(request.items) || request.items.length < 1 || request.items.length > 5000) return fail();
  const products = new Set<string>(), pairs = new Set<string>();
  for (const item of request.items) {
    if (!object(item) || !keys(item, ["productId", "plannedQuantity", "sourceRows"], ["unitCostMinor"])
      || !identifier(item.productId) || products.has(item.productId.toLowerCase())
      || !Number.isSafeInteger(item.plannedQuantity) || Number(item.plannedQuantity) < 1 || Number(item.plannedQuantity) > 999_999
      || item.unitCostMinor !== undefined && item.unitCostMinor !== null && (!Number.isSafeInteger(item.unitCostMinor) || Number(item.unitCostMinor) < 0 || Number(item.unitCostMinor) > 9_000_000_000_000)
      || !Array.isArray(item.sourceRows) || item.sourceRows.length < 1 || item.sourceRows.length > 1000) return fail();
    products.add(item.productId.toLowerCase());
    let quantity = 0;
    for (const row of item.sourceRows) {
      if (!object(row) || !keys(row, ["sourceProductId", "sourceWarehouseId", "quantity"])
        || !identifier(row.sourceProductId) || !identifier(row.sourceWarehouseId)
        || typeof row.quantity !== "string" || !/^[1-9]\d{0,5}$/.test(row.quantity)) return fail();
      const pair = `${row.sourceProductId.toLowerCase()}:${row.sourceWarehouseId.toLowerCase()}`;
      if (pairs.has(pair)) return fail();
      pairs.add(pair); quantity += Number(row.quantity);
    }
    if (quantity !== item.plannedQuantity) return fail();
  }
  return Object.freeze(value as OpeningSourcePackage);
}
